import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { PublicRoot } from './pages/public/PublicRoot';
import { parsePublicPath } from './lib/public-router';
import { SettingsStore } from './lib/settings';
import { themeFromUrl } from './lib/theme';
import './styles.css';
import './theme-var.css';

// El estado de configuración vive fuera de React (mismo patrón que la captura).
// El constructor ya aplica el tema guardado a `<html data-theme>`.
const store = new SettingsStore();

// `?tema=clasico|var` gana sobre lo guardado y se recuerda: es la puerta del
// portal público, que no tiene Ajustes. Todo antes del primer render.
const urlTheme = themeFromUrl(window.location.search, window.location.hash);
if (urlTheme !== null && urlTheme !== store.getSnapshot().theme) {
  store.set({ ...store.getSnapshot(), theme: urlTheme });
}

/**
 * DOS PORTALES, UN BUNDLE.
 *
 * El portal interno rutea por HASH (`#/matches`) y el público por PATH
 * (`/feva`, `/feva/partido/x`). Conviven en el mismo origen y el mismo build a
 * propósito: el público tiene que ser `liga.org/feva` —una URL que se imprime
 * en el programa del partido y se dicta por teléfono— y eso ya funciona sin
 * tocar el despliegue, porque `deploy/nginx.conf` sirve el `index.html` del
 * portal para cualquier path que cuelgue de él.
 *
 * Quién manda: si el path resuelve a una ruta pública, se monta el portal
 * público; si no, el de siempre. Ver `lib/public-router.ts` para la gramática y
 * para por qué el widget lleva el slug adelante.
 */
/**
 * Base real del portal. Con `--base=/portal/` Vite la deja en BASE_URL; con
 * `--base=./` (modo cancha, `npm run cancha`) BASE_URL es './' y no dice bajo
 * qué prefijo se sirve la app, así que se mira el path: si empieza con
 * `/portal/` (API con SCOUT_SERVE_WEB, nginx), esa es la base; si no, la raíz.
 */
function resolvePortalBase(): string {
  const configured = import.meta.env.BASE_URL;
  if (configured.startsWith('/')) return configured;
  return window.location.pathname.startsWith('/portal/') ? '/portal/' : '/';
}

const portalBase = resolvePortalBase();
const publicRoute = parsePublicPath(window.location.pathname, portalBase, window.location.search);

// Sin hash Y sin ruta pública, la portada es el archivo de partidos. Con ruta
// pública NO se toca el hash: ensuciaría una URL que la gente comparte.
if (publicRoute === null && window.location.hash === '') window.location.hash = '#/matches';

const container = document.getElementById('root');
if (!container) throw new Error('Falta #root en index.html');

createRoot(container).render(
  <StrictMode>
    {publicRoute === null ? (
      <App store={store} />
    ) : (
      <PublicRoot base={portalBase} apiUrl={store.getSnapshot().apiUrl} />
    )}
  </StrictMode>,
);
